import { CarId, NetworkEvent, PlayState, Topology } from "./protocol";

const generatedPathIds = (): string[] => {
  const ids: string[] = [];
  for (let input = 0; input < 7; input += 1) {
    ids.push(`input.${input}.encoder`);
    for (let driver = 0; driver < 4; driver += 1) {
      for (let branch = 0; branch < 6; branch += 1) ids.push(`encoder.${input}.driver.${driver}.branch.${branch}`);
    }
  }
  for (let driver = 0; driver < 4; driver += 1) {
    for (let branch = 0; branch < 6; branch += 1) ids.push(`driver.${driver}.branch.${branch}.output`);
  }
  [12, 7, 12].forEach((count, vehicle) => {
    for (let branch = 0; branch < count; branch += 1) {
      ids.push(`vehicle.${vehicle}.input.branch.${branch}`);
      ids.push(`vehicle.${vehicle}.branch.${branch}.output`);
    }
  });
  return ids;
};

export const GOAL_NETWORK_PATH_IDS = new Set([
  ...generatedPathIds(),
  "physical.driver-steering-turn",
  "physical.driver-throttle-drive",
  "physical.driver-brake-brake",
  "physical.driver-gear-drive",
  "physical.speed-turn",
  "physical.speed-brake",
  "physical.vehicle-turn-state",
  "physical.vehicle-drive-state",
  "physical.vehicle-brake-state",
]);

export interface GoalNetworkWindow {
  carId: CarId;
  seekGeneration: number;
  playState: PlayState;
  previousRaceTimeMs: number;
  raceTimeMs: number;
}

export type GoalNetworkPulseShape = "circle";

export const goalNetworkPulseShape = (direction: string): GoalNetworkPulseShape | null => {
  if (direction === "upward_packet"
    || direction === "downward_target"
    || direction === "physical_signal") return "circle";
  return null;
};

export const selectGoalNetworkPulses = (
  events: NetworkEvent[],
  window: GoalNetworkWindow,
): NetworkEvent[] => {
  if (window.playState !== "playing" || window.raceTimeMs < window.previousRaceTimeMs) return [];
  return events.filter(event =>
    event.car_id === window.carId
    && event.seek_generation === window.seekGeneration
    && event.race_time_ms > window.previousRaceTimeMs
    && event.race_time_ms <= window.raceTimeMs
    && goalNetworkPulseShape(event.direction) !== null
    && typeof event.display_path_id === "string"
    && GOAL_NETWORK_PATH_IDS.has(event.display_path_id));
};

export const currentGoalNetworkReadouts = (
  events: NetworkEvent[],
  carId: CarId,
  seekGeneration: number,
  raceTimeMs: number,
): Map<string, NetworkEvent["readout"]> => {
  const latest = latestReadoutEvents(events, carId, seekGeneration, raceTimeMs);
  return new Map([...latest].map(([key, event]) => [key, event.readout]));
};

const latestReadoutEvents = (
  events: NetworkEvent[], carId: CarId, seekGeneration: number, raceTimeMs: number,
): Map<string, NetworkEvent> => {
  const latest = new Map<string, NetworkEvent>();
  for (const event of eligibleEvents(events, carId, seekGeneration, raceTimeMs)) {
    if (!event.readout) continue;
    const previous = latest.get(event.readout.readout_id);
    if (!previous || previous.race_time_ms <= event.race_time_ms) latest.set(event.readout.readout_id, event);
  }
  return latest;
};

export const currentGoalNetworkReadoutTicks = (
  events: NetworkEvent[], carId: CarId, seekGeneration: number, raceTimeMs: number,
): Map<string, string> => new Map([...latestReadoutEvents(events, carId, seekGeneration, raceTimeMs)]
  .map(([key, event]) => [key, (event.causal_tick_id ?? `tick/${event.tick_index ?? "?"}`).split("/").at(-1) ?? "?"]));

const eligibleEvents = (
  events: NetworkEvent[], carId: CarId, seekGeneration: number, raceTimeMs: number,
): NetworkEvent[] => events.filter(event => event.car_id === carId
  && event.seek_generation === seekGeneration && event.race_time_ms <= raceTimeMs);

export interface GoalPathDynamics {
  branchWeight?: number;
  weightedContribution?: number;
  intensity: number;
}

export const currentGoalNetworkDynamics = (
  events: NetworkEvent[], window: Pick<GoalNetworkWindow, "carId" | "seekGeneration" | "playState" | "raceTimeMs">,
): Map<string, GoalPathDynamics> => {
  if (window.playState !== "playing") return new Map();
  const candidates = eligibleEvents(events, window.carId, window.seekGeneration, window.raceTimeMs)
    .filter(event => event.display_path_id && event.payload
      && (typeof event.payload.branch_weight === "number" || Array.isArray(event.payload.weighted_contribution)));
  const latestByPath = new Map<string, number>();
  for (const event of candidates) {
    const path = event.display_path_id!;
    latestByPath.set(path, Math.max(latestByPath.get(path) ?? -Infinity, event.race_time_ms));
  }
  const snapshot = candidates.filter(event => event.race_time_ms === latestByPath.get(event.display_path_id!));
  const raw = new Map<string, { branchWeight?: number; weightedContribution?: number }>();
  for (const event of snapshot) {
    const path = event.display_path_id;
    const payload = event.payload;
    if (!path || !GOAL_NETWORK_PATH_IDS.has(path) || !payload) continue;
    const record = raw.get(path) ?? {};
    if (typeof payload.branch_weight === "number" && Number.isFinite(payload.branch_weight)) {
      record.branchWeight = Math.abs(payload.branch_weight);
    }
    const contribution = payload.weighted_contribution;
    if (Array.isArray(contribution) && contribution.length === 2
      && contribution.every(value => typeof value === "number" && Number.isFinite(value))) {
      record.weightedContribution = Math.hypot(contribution[0], contribution[1]);
    }
    if (record.branchWeight !== undefined || record.weightedContribution !== undefined) raw.set(path, record);
  }
  const contributionScale = Math.max(0, ...[...raw.values()].map(value => value.weightedContribution ?? 0));
  return new Map([...raw].map(([path, value]) => {
    const weightIntensity = Math.min(1, value.branchWeight ?? 0);
    const contributionIntensity = contributionScale > 0 ? (value.weightedContribution ?? 0) / contributionScale : 0;
    return [path, { ...value, intensity: Math.max(weightIntensity, contributionIntensity) }];
  }));
};

export interface GoalCurrentValue { value: string; unit: string; tick: string }

const formatCurrentValue = (value: unknown): string => {
  if (typeof value === "boolean") return value ? "true" : "false";
  return typeof value === "string" || typeof value === "number" ? String(value) : "—";
};

export const currentGoalNetworkInputValues = (
  events: NetworkEvent[], carId: CarId, seekGeneration: number, raceTimeMs: number,
): Map<string, GoalCurrentValue> => {
  const latest = new Map<string, NetworkEvent>();
  for (const event of eligibleEvents(events, carId, seekGeneration, raceTimeMs)) {
    if (event.stage !== "encoder_output" || !event.display_path_id) continue;
    const previous = latest.get(event.display_path_id);
    if (!previous || previous.race_time_ms <= event.race_time_ms) latest.set(event.display_path_id, event);
  }
  return new Map([...latest].map(([path, event]) => [path, {
    value: formatCurrentValue(event.payload?.current_value),
    unit: typeof event.payload?.unit === "string" ? event.payload.unit : "",
    tick: event.causal_tick_id ?? `tick/${event.tick_index ?? "?"}`,
  }]));
};

export const currentGoalVehicleInputs = (
  events: NetworkEvent[], carId: CarId, seekGeneration: number, raceTimeMs: number,
): Map<string, GoalCurrentValue> => {
  const latest = new Map<string, NetworkEvent>();
  for (const event of eligibleEvents(events, carId, seekGeneration, raceTimeMs)) {
    if (event.stage !== "physical_boundary_input" || !event.display_path_id) continue;
    const previous = latest.get(event.display_path_id);
    if (!previous || previous.race_time_ms <= event.race_time_ms) latest.set(event.display_path_id, event);
  }
  return new Map([...latest].map(([path, event]) => {
    const rawThrottle = event.payload?.raw_input_value;
    const value = rawThrottle === undefined
      ? formatCurrentValue(event.payload?.input_value)
      : `${formatCurrentValue(rawThrottle)} raw / ${formatCurrentValue(event.payload?.input_value)} above-floor`;
    return [path, {
      value,
      unit: typeof event.payload?.input_unit === "string" ? event.payload.input_unit : "",
      tick: event.causal_tick_id ?? `tick/${event.tick_index ?? "?"}`,
    }];
  }));
};

export const goalLearningStatusLabel = (status: Topology["learning_status"]): string => {
  if (status === undefined) return "LEARNING STATUS UNKNOWN";
  const connection = status.connected === true ? "LEARNING CONNECTED"
    : status.connected === false ? "LEARNING DISCONNECTED" : "LEARNING CONNECTION UNKNOWN";
  const writes = typeof status.parameter_writes === "number"
    ? `${status.parameter_writes} WRITES` : "WRITES UNKNOWN";
  return `${connection} / ${writes}`;
};
